/**
 * Dictionary mapping utility
 * Maps dictionary keys to actual translated values using flat structure
 */

/**
 * Gets a value from a flat dictionary using a key with failsafe
 * @param dict - The flat dictionary object
 * @param key - The key to look up (e.g., "hero_title")
 * @returns The value for the key or the key itself if not found
 */
export function getDictValue(dict: any, key: string): string {
  try {
    if (!dict || typeof dict !== 'object' || !key || typeof key !== 'string') {
      console.warn(dict?.errors_dictionaryNotAvailable || 'Dictionary is not available');
      return key; // Return the key itself as fallback
    }
    
    const value = dict[key];
    
    if (value === undefined || value === null || value === '') {
      console.warn(dict?.errors_keyNotFound || 'Dictionary key not found');
      return key; // Return the key itself as fallback
    }
    
    return String(value);
  } catch (error) {
    console.error(dict?.errors_accessingKey || 'Error accessing dictionary key', error);
    return key; // Return the key itself as fallback
  }
}

/**
 * Legacy function for backward compatibility - now uses flat structure
 * @deprecated Use getDictValue instead
 */
export function getNestedValue(obj: any, key: string): string {
  // Convert dot notation to underscore notation for flat dictionary
  const flatKey = key.replace(/\./g, '_');
  return getDictValue(obj, flatKey);
}

/**
 * Maps dictionary keys in data to actual translated values with failsafe
 * @param data - The data object containing dictionary keys
 * @param dict - The dictionary object containing translations
 * @returns The data object with keys replaced by translated values
 */
export function mapDictKeys(data: any, dict: any): any {
  try {
    if (typeof data === 'string' && data.includes('.')) {
      // If it's a string with dots, it's likely a dictionary key
      return getNestedValue(dict, data);
    }
    
    if (typeof data === 'object' && data !== null) {
      if (Array.isArray(data)) {
        // If it's an array, map each item
        return data.map(item => mapDictKeys(item, dict));
      } else {
        // If it's an object, map each property
        const mapped: any = {};
        for (const [key, value] of Object.entries(data)) {
          mapped[key] = mapDictKeys(value, dict);
        }
        return mapped;
      }
    }
    
    // If it's not a string with dots or an object, return as is
    return data;
  } catch (error) {
    console.error('Error in mapDictKeys:', error);
    return data; // Return original data if mapping fails
  }
}

/**
 * Safe dictionary mapping with comprehensive error handling
 * @param data - The data object to map
 * @param dict - The dictionary object
 * @param fallbackData - Fallback data to use if mapping fails
 * @returns Mapped data or fallback data
 */
export function safeMapDictKeys(data: any, dict: any, fallbackData?: any): any {
  try {
    if (!dict || typeof dict !== 'object') {
      console.warn(dict?.errors_dictionaryNotAvailable || 'Dictionary not available, using fallback data');
      return fallbackData || data;
    }
    
    return mapDictKeys(data, dict);
  } catch (error) {
    console.error(dict?.errors_dictionaryMappingFailed || 'Safe dictionary mapping failed:', error);
    return fallbackData || data;
  }
}
